.panel {
  background: var(--surface);
  border-radius: var(--radius);
  padding: 24px;
  min-width: 0;
}

.heading {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 18px;
}

.label {
  font-family: var(--font-display);
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--text);
}

.toggle {
  display: flex;
  gap: 2px;
  background: var(--surface-2);
  border-radius: 6px;
  padding: 2px;
}

.toggleBtn {
  background: none;
  border: none;
  padding: 4px 10px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--soft);
  border-radius: 5px;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}

.toggleBtn:hover {
  color: var(--text);
}

.toggleActive {
  background: var(--canvas);
  color: var(--text);
  box-shadow: 0 1px 3px rgba(38, 33, 25, 0.12);
}

.list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.row {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 14px 0;
  border-bottom: 1px solid var(--border);
}

.row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.rank {
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
  width: 22px;
  flex-shrink: 0;
  padding-top: 2px;
  text-align: right;
}

/* 20px rather than 10: the toggle's hover box bleeds 10px past its content on
   every side, so the open row keeps the same visible gap above the details. */
.info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

/* Padding and matching negative margin leave the layout where it was and give
   the hover tint room to read as a row. */
.rowToggle {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  margin: -10px;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  text-align: left;
  color: inherit;
  font: inherit;
  cursor: pointer;
  transition: background 0.15s;
}

/* The whole row is the click target; the chevron alone was too small to see. */
.rowToggle:hover {
  background: var(--tint);
}

/* The name owns the full width and wraps to a second line rather than being
   cut with an ellipsis — on a phone the truncated half was the half that told
   you which variant of the lift it was. */
.infoTop {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.name {
  flex: 1;
  min-width: 0;
  font-size: 1rem;
  font-weight: 600;
  line-height: 1.35;
  color: var(--text);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  overflow-wrap: anywhere;
}

.chevron {
  flex-shrink: 0;
  color: var(--muted);
  font-size: 1rem;
  line-height: 1.3;
  transform: rotate(90deg);
  transition: transform 0.2s;
}

.chevronOpen {
  transform: rotate(270deg);
}

.rowToggle:hover .chevron {
  color: var(--text);
}

.barTrack {
  height: 6px;
  background: var(--surface-2);
  border-radius: 999px;
  overflow: hidden;
}

.barFill {
  height: 100%;
  border-radius: inherit;
  background: var(--amber);
  opacity: 0.7;
  transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

/* One wrapping line: the bar's own value, then the counts it is not built
   from, then the badges. */
.meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 10px;
  row-gap: 6px;
}

.metric {
  font-size: 0.95rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: var(--amber-strong);
  white-space: nowrap;
}

.metricSub {
  font-size: 0.85rem;
  color: var(--muted);
}

/* Badges — a tinted pill each, so they read as one chip instead of blending
   into the counts beside them. Direction never rests on colour alone: the
   arrow and the signed percentage carry it too. */
.badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  font-size: 0.8rem;
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;
}

.badgeUp {
  color: var(--up);
  background: var(--up-dim);
}

.badgeFlat {
  color: var(--flat);
  background: var(--surface-2);
}

.badgeDown {
  color: var(--down);
  background: var(--down-dim);
}

.badgePr {
  color: var(--amber-strong);
  background: var(--amber-dim);
}

/* The status word itself, for screen readers — the pill only has room for the
   number. */
.badgeReader {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.details {
  display: flex;
  flex-direction: column;
  gap: 10px;
  border-top: 1px dashed var(--border);
  margin-top: 2px;
  padding-top: 10px;
}

/* What the badge is built from, spelled out — a phone has no hover, so the
   tooltip alone left the number unexplained. */
.trendNote {
  margin: 0;
  font-size: 0.85rem;
  line-height: 1.55;
  color: var(--muted);
}

.trendNoteHead {
  font-weight: 600;
  white-space: nowrap;
}

.textUp   { color: var(--up); }
.textFlat { color: var(--flat); }
.textDown { color: var(--down); }
.textPr   { color: var(--amber-strong); }

.records {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.recordCaption {
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--soft);
  margin-bottom: 2px;
}

.recordRow {
  display: grid;
  grid-template-columns: 1fr auto auto;
  align-items: baseline;
  gap: 10px;
  font-size: 0.88rem;
}

/* Wraps rather than truncates: an ellipsis here ate the NEW badge, which is
   the one part of the row that is not repeated anywhere else. */
.recordLabel {
  color: var(--muted);
  min-width: 0;
  overflow-wrap: anywhere;
}

.newBadge {
  display: inline-block;
  background: var(--amber-dim);
  color: var(--amber-strong);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  border-radius: 999px;
  padding: 1px 7px;
  margin-left: 5px;
  vertical-align: middle;
  line-height: 1.5;
}

.recordValue {
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: var(--text);
  white-space: nowrap;
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.recordSub {
  color: var(--muted);
  font-size: 0.8rem;
  font-weight: 400;
}

.recordLink {
  background: none;
  border: none;
  padding: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--amber-strong);
  cursor: pointer;
  white-space: nowrap;
}

.recordLink:hover {
  text-decoration: underline;
}

.recordDate {
  font-size: 0.82rem;
  font-variant-numeric: tabular-nums;
  color: var(--muted);
  white-space: nowrap;
}

.recordEmpty {
  font-size: 0.85rem;
  color: var(--muted);
}

/* Why two record blocks can disagree — the caption alone cannot carry it. */
.note {
  margin: 0 0 2px;
  font-size: 0.8rem;
  line-height: 1.5;
  color: var(--muted);
}

/* The long part of the panel, so it reads as rows rather than a table: when,
   which workout, what happened in it. */
.sessions {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.sessionRow {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: baseline;
  gap: 2px 10px;
  width: 100%;
  padding: 5px 8px;
  margin: 0 -8px;
  background: none;
  border: none;
  border-radius: var(--radius-sm);
  text-align: left;
  font: inherit;
  font-size: 0.85rem;
  color: inherit;
}

.sessionLink {
  cursor: pointer;
  transition: background 0.15s;
}

.sessionLink:hover {
  background: var(--tint);
}

.sessionDate {
  font-variant-numeric: tabular-nums;
  color: var(--soft);
  white-space: nowrap;
}

/* The workout name is the one part worth losing — it repeats on the workouts
   tab, where the row links to. */
.sessionName {
  min-width: 0;
  color: var(--muted);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sessionSummary {
  color: var(--text);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.sessionPr {
  margin-left: 6px;
  color: var(--amber-strong);
  white-space: nowrap;
}

.moreBtn {
  align-self: flex-start;
  margin-top: 4px;
  padding: 0;
  background: none;
  border: none;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--amber-strong);
  cursor: pointer;
}

.moreBtn:hover {
  text-decoration: underline;
}

.empty {
  display: grid;
  place-items: center;
  min-height: 120px;
  font-size: 0.9rem;
  color: var(--muted);
}

@media (max-width: 420px) {
  .toggleBtn {
    padding: 3px 7px;
  }

  .row {
    gap: 12px;
  }

  .meta {
    column-gap: 8px;
  }
}

/* Label, value and date are three things too many for one phone-width line —
   squeezing them cost the label its NEW badge. The date drops to its own line
   instead, where it still reads as the record's when. */
@media (max-width: 480px) {
  .recordRow {
    grid-template-columns: 1fr auto;
    row-gap: 1px;
  }

  .recordLink,
  .recordDate {
    grid-column: 1 / -1;
    text-align: right;
  }

  /* Same squeeze on a session row: the summary keeps its own line so the top
     set never wraps mid-number. */
  .sessionRow {
    grid-template-columns: auto 1fr;
  }

  .sessionSummary {
    grid-column: 1 / -1;
    white-space: normal;
  }
}
